import type { ReactNode } from "react";
import { logoutProductionSession } from "../lib/api/authClient";
import { clearDemoSession, getDemoSession } from "../lib/demoAuth";
import { t, useWorkspaceLanguage, withLanguagePrefix } from "../lib/i18n";
import { BrandHeading, HauffeLogo } from "./BrandLogo";
import { LanguageSwitcher } from "./LanguageSwitcher";

interface LayoutProps {
  eyebrow?: string;
  title: string;
  description?: string;
  children: ReactNode;
}

export function Layout({ eyebrow, title, description, children }: LayoutProps) {
  const session = getDemoSession();
  const language = useWorkspaceLanguage();
  const labels = t(language);
  const legalLinks = language === "en"
    ? {
        imprint: { href: "https://hauffe.io/en/legal-notice/", label: "Legal Notice" },
        terms: { href: "/terms", label: "Terms" },
        privacy: { href: "/privacy", label: "Privacy" }
      }
    : {
        imprint: { href: "https://hauffe.io/de/impressum/", label: "Impressum" },
        terms: { href: "/agb", label: "AGB" },
        privacy: { href: "/datenschutz", label: "Datenschutz" }
      };
  const navItems = session
    ? session.role === "hauffe_admin"
      ? [
          { href: "/admin", label: labels.layout.nav.overview },
          { href: "/admin", label: labels.layout.nav.workspaces },
          { href: "/admin/workspaces/new", label: labels.layout.nav.newWorkspace },
          { href: "/admin/users", label: labels.layout.nav.users },
          { href: "/workspace/deal-win-engine", label: labels.layout.nav.dealWinEngine }
        ]
      : session.role === "company_owner"
        ? [
            { href: "/workspace", label: labels.layout.nav.workspace },
            { href: "/workspace/departments", label: labels.layout.nav.departments },
            { href: "/workspace/hauffe-os-summary", label: labels.layout.nav.evaluation },
            { href: "/workspace/customer-value", label: labels.layout.nav.customerValue },
            { href: "/workspace/plans", label: labels.layout.nav.myPlans }
          ]
        : [{ href: "/contributor", label: labels.layout.nav.contributor }]
    : [
        { href: "/", label: labels.layout.nav.entry },
        { href: "/pricing", label: labels.layout.nav.pricing },
        { href: "/login", label: labels.layout.nav.login }
      ];

  async function logout() {
    await logoutProductionSession();
    clearDemoSession();
    window.location.assign(withLanguagePrefix("/login", language));
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <a className="brand" href={withLanguagePrefix("/", language)} aria-label="HAUFFE OS Customer Value Workspace">
          <HauffeLogo language={language} />
        </a>
        <div className="header-actions">
          <nav className="main-nav" aria-label={language === "en" ? "Main navigation" : "Hauptnavigation"}>
            {navItems.map((item) => (
              <a key={item.href} href={withLanguagePrefix(item.href, language)}>
                {item.label}
              </a>
            ))}
            {session ? (
              <button className="nav-button" type="button" onClick={logout}>
                {labels.layout.nav.logout}
              </button>
            ) : null}
          </nav>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="main-content">
        <section className="page-hero">
          {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
          <BrandHeading level="h1" language={language}>{title}</BrandHeading>
          {description ? <p className="hero-copy">{description}</p> : null}
        </section>
        {children}
      </main>
      <footer className="app-footer">
        <span>© 2026 HAUFFE OS</span>
        <nav aria-label={language === "en" ? "Legal links" : "Rechtliche Links"}>
          <a href={legalLinks.imprint.href}>{legalLinks.imprint.label}</a>
          <a href={withLanguagePrefix(legalLinks.terms.href, language)}>{legalLinks.terms.label}</a>
          <a href={withLanguagePrefix(legalLinks.privacy.href, language)}>{legalLinks.privacy.label}</a>
        </nav>
      </footer>
    </div>
  );
}
